import React, { useEffect, useMemo, useRef, useState } from 'react';
import { listSupperTeachersApi, type SupperTeacherOption } from '../services/trungTamApi';

type Props = {
  value: number | null | undefined;
  onChange: (teacherId: number | null, fullName: string | null) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Optional — pre-supplied display name, shown before the full list
   *  loads so the field doesn't flash "empty" when opening a saved record. */
  initialDisplayName?: string;
};

// Module-level in-memory cache — the supper teacher list is small
// (usually 5-30 rows) and changes rarely. Every mount hitting /api
// would be wasteful. 5-minute TTL. Shared across instances.
type CacheEntry = { list: SupperTeacherOption[]; ts: number };
let cache: CacheEntry | null = null;
const CACHE_TTL_MS = 5 * 60 * 1000;

const loadTeachers = async (): Promise<SupperTeacherOption[]> => {
  if (cache && Date.now() - cache.ts < CACHE_TTL_MS) return cache.list;
  try {
    const resp = await listSupperTeachersApi();
    if (resp.EC === 0 && Array.isArray(resp.DT)) {
      cache = { list: resp.DT, ts: Date.now() };
      return resp.DT;
    }
  } catch { /* swallow — autocomplete shows empty */ }
  return [];
};

/**
 * Autocomplete picker for "Giáo viên phụ trách".
 * - Fetches (and caches) the supper teacher list on first focus.
 * - Filters as the operator types — simple substring match on fullName/email.
 * - Keyboard: ↑/↓ navigate suggestions, Enter select, Esc close.
 */
const TeacherAutocomplete: React.FC<Props> = ({
  value, onChange, disabled, placeholder = 'Chọn giáo viên phụ trách…', initialDisplayName,
}) => {
  const [list, setList] = useState<SupperTeacherOption[]>(() => cache?.list || []);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Resolve display text for the current value — uses cached list first,
  // falls back to initialDisplayName so the row-click → detail-modal
  // doesn't flash blank while the list loads.
  const selected = useMemo(
    () => list.find(t => t.id === value) || null,
    [list, value],
  );
  const displayText = selected
    ? `${selected.fullName}${selected.email ? ` — ${selected.email}` : ''}`
    : initialDisplayName || '';

  useEffect(() => {
    let alive = true;
    void loadTeachers().then(l => { if (alive) setList(l); });
    return () => { alive = false; };
  }, []);

  // Close dropdown on outside click.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return list.slice(0, 20);
    return list
      .filter(t =>
        t.fullName.toLowerCase().includes(q) ||
        t.email.toLowerCase().includes(q),
      )
      .slice(0, 20);
  }, [list, query]);

  const commit = (t: SupperTeacherOption | null) => {
    onChange(t?.id ?? null, t?.fullName ?? null);
    setQuery('');
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[activeIdx]) commit(filtered[activeIdx]);
    }
    else if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div
      ref={wrapRef}
      className="tt-teacher-autocomplete"
      style={{ position: 'relative', width: '100%' }}
    >
      {value && selected && !open ? (
        // Selected state — chip with × clear.
        <div
          className="tt-teacher-autocomplete__chip"
          onClick={() => { if (!disabled) { setOpen(true); setQuery(''); setTimeout(() => inputRef.current?.focus(), 0); } }}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 6,
            padding: '6px 10px',
            border: '1px solid #c5d6e6',
            borderRadius: 6,
            background: '#f0f6fc',
            cursor: disabled ? 'default' : 'pointer',
            minHeight: 34,
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.85rem' }}>
            <i className="material-icons" style={{ fontSize: 16, color: '#1976d2' }}>person</i>
            <strong>{selected.fullName}</strong>
            {selected.email && <span style={{ color: '#666', fontSize: '0.75rem' }}>{selected.email}</span>}
          </span>
          {!disabled && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); commit(null); }}
              title="Bỏ chọn"
              style={{
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                padding: 2,
                display: 'flex',
              }}
            >
              <i className="material-icons" style={{ fontSize: 16, color: '#888' }}>close</i>
            </button>
          )}
        </div>
      ) : (
        <input
          ref={inputRef}
          type="text"
          value={open ? query : displayText}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); setActiveIdx(0); }}
          onFocus={() => { if (!disabled) { setOpen(true); setQuery(''); } }}
          onKeyDown={onKeyDown}
          disabled={disabled}
          placeholder={placeholder}
          autoComplete="off"
          style={{
            width: '100%',
            padding: '6px 10px',
            border: '1px solid #d0d7de',
            borderRadius: 6,
            fontSize: '0.85rem',
            background: disabled ? '#f5f5f5' : '#fff',
            minHeight: 34,
          }}
        />
      )}

      {open && !disabled && (
        <div
          className="tt-teacher-autocomplete__menu"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 4,
            maxHeight: 260,
            overflowY: 'auto',
            background: '#fff',
            border: '1px solid #d0d7de',
            borderRadius: 6,
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
            zIndex: 50,
          }}
        >
          {filtered.length === 0 ? (
            <div style={{ padding: 10, color: '#888', fontSize: '0.82rem', textAlign: 'center' }}>
              {list.length === 0 ? 'Đang tải danh sách…' : 'Không có giáo viên phù hợp'}
            </div>
          ) : (
            filtered.map((t, idx) => (
              <button
                type="button"
                key={t.id}
                onMouseDown={(e) => { e.preventDefault(); commit(t); }}
                onMouseEnter={() => setActiveIdx(idx)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 10px',
                  border: 'none',
                  background: idx === activeIdx ? '#e7f1ff' : '#fff',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  borderBottom: '1px solid #eef0f2',
                }}
              >
                <div style={{ fontWeight: 600, color: '#222' }}>{t.fullName}</div>
                {t.email && <div style={{ fontSize: '0.72rem', color: '#666' }}>{t.email}</div>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default TeacherAutocomplete;
